import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown, Search, Settings, X } from 'lucide-react'
import { useModules } from '../../contexts/ModuleContext'
import { useIsMobileLayout } from '../../hooks/useMediaQuery'
import { lsGet, lsSet, NavItem, PinnedSection, RecentSection } from './NavSections'
import { useFlag } from '../../hooks/useFlag'
import { HOME_FLAG, getAccordionSections, getActiveWorkspaceRoute, getNavigationDisplayItem, isSidebarWorkRoute, withHomeEntry } from './workspaceNavigationModel'
import { searchNavigation } from './navigationSearch'
import { revealNavigationSection } from './navigationScroll'

const ACCORDION_KEY = 'onboardix_nav_accordion_v1'
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]'

export default function WorkspaceNavigation({ effectiveUser, personalNav, allItems, pinnedRoutes, recentRoutes, onTogglePin, mobileOpen, onClose, triggerRef }) {
    const { isModuleVisible } = useModules()
    const location = useLocation()
    const isMobile = useIsMobileLayout()
    const sidebarRef = useRef(null)
    const navRef = useRef(null)
    const pendingSection = useRef(null)
    const [query, setQuery] = useState('')
    const [openSection, setOpenSection] = useState(() => lsGet(ACCORDION_KEY, null))
    const { enabled: homeEnabled } = useFlag(HOME_FLAG)
    const sections = useMemo(() => withHomeEntry(getAccordionSections(effectiveUser, personalNav,
        key => isModuleVisible(key, effectiveUser)), homeEnabled), [effectiveUser, personalNav, isModuleVisible, homeEnabled])
    const activeRoute = getActiveWorkspaceRoute(sections.flatMap(section => section.items), location.pathname)
    const visibleItems = useMemo(() => allItems.filter(item => isSidebarWorkRoute(item) && isModuleVisible(item.moduleKey || item.to || 'dashboard', effectiveUser))
        .map(item => ({ ...getNavigationDisplayItem(item), activeOverride: item.to === activeRoute })), [allItems, isModuleVisible, effectiveUser, activeRoute])
    const results = useMemo(() => searchNavigation(sections.flatMap(section => section.items), query), [sections, query])
    const activeSection = sections.find(section => section.items.some(item => item.to === activeRoute))?.title
    // A new route (including browser Back) opens its containing group. Manual
    // collapse stays effective until the route or visible inventory changes.
    useEffect(() => { setOpenSection(activeSection || null) }, [location.pathname, activeSection])
    const drawerOpen = isMobile && mobileOpen

    useEffect(() => {
        if (!drawerOpen) return undefined
        const previousOverflow = document.body.style.overflow
        const trigger = triggerRef.current
        document.body.style.overflow = 'hidden'
        // A visibility transition starts at hidden. Focus on the next paint,
        // when the browser can focus the opened drawer's controls.
        const focusFrame = requestAnimationFrame(() => sidebarRef.current?.querySelector(FOCUSABLE)?.focus())
        const onKey = event => {
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); return }
            if (event.key !== 'Tab') return
            const items = [...sidebarRef.current.querySelectorAll(FOCUSABLE)].filter(item => item.getClientRects().length > 0)
            const first = items[0], last = items.at(-1)
            if (!first) return
            if (!sidebarRef.current.contains(document.activeElement)) { event.preventDefault(); first.focus(); return }
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
        }
        document.addEventListener('keydown', onKey)
        return () => {
            cancelAnimationFrame(focusFrame)
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKey)
            trigger?.focus()
        }
    }, [drawerOpen, onClose, triggerRef])

    useEffect(() => {
        const frame = requestAnimationFrame(() => sidebarRef.current?.querySelector('.nav-link.active')?.scrollIntoView?.({ block: 'nearest' }))
        return () => cancelAnimationFrame(frame)
    }, [location.pathname, activeSection])

    useEffect(() => {
        const title = pendingSection.current
        pendingSection.current = null
        if (!title || title !== openSection) return undefined
        const frame = requestAnimationFrame(() => {
            const group = [...(navRef.current?.querySelectorAll('.workspace-navigation__group') || [])]
                .find(element => element.querySelector('button')?.textContent === title)
            revealNavigationSection(navRef.current, group)
        })
        return () => cancelAnimationFrame(frame)
    }, [openSection])

    const toggleSection = title => setOpenSection(previous => {
        const next = previous === title ? null : title
        pendingSection.current = next
        lsSet(ACCORDION_KEY, next)
        return next
    })
    const renderItem = item => <NavItem key={item.to} item={{ ...item, activeOverride: item.to === activeRoute }} onNavigate={() => { setQuery(''); onClose() }}
        isPinned={pinnedRoutes.has(item.to)} onTogglePin={onTogglePin} />

    return (
        <>
            {drawerOpen && <div className="layout__backdrop" onClick={onClose} aria-hidden="true" />}
            <aside ref={sidebarRef} id="workspace-navigation" className={`sidebar${drawerOpen ? ' open' : ''}`} role={drawerOpen ? 'dialog' : undefined} aria-modal={drawerOpen || undefined} aria-label="Навигация Onboardix" inert={isMobile && !drawerOpen ? true : undefined}>
                <div className="workspace-navigation__mobile-heading">
                    <span>Рабочее пространство</span>
                    <button type="button" className="topbar__icon-btn" onClick={onClose} aria-label="Закрыть навигацию"><X size={20} /></button>
                </div>
                <div className="workspace-navigation__search-header">
                    <div className="workspace-navigation__search">
                        <Search size={16} aria-hidden="true" />
                        <input type="search" aria-label="Поиск разделов" placeholder="Найти раздел…" value={query} onChange={event => setQuery(event.target.value)}
                            onKeyDown={event => { if (event.key === 'Escape' && query) { event.preventDefault(); event.stopPropagation(); setQuery('') } }} />
                    </div>
                </div>
                <nav ref={navRef} className="sidebar__nav" aria-label="Разделы приложения">
                    {!query.trim() && <>
                        <PinnedSection pinnedRoutes={pinnedRoutes} allItems={visibleItems} onNavigate={onClose} onTogglePin={onTogglePin} />
                        <RecentSection recentRoutes={recentRoutes.filter(route => !pinnedRoutes.has(route))} allItems={visibleItems} onNavigate={onClose} />
                    </>}
                    {query.trim() ? <div className="workspace-navigation__results" aria-label="Результаты поиска">
                        <p className="workspace-navigation__search-status" role="status">{results.length ? `Найдено: ${results.length}` : 'Разделы не найдены'}</p>
                        {results.map(renderItem)}
                    </div> : <div className="workspace-navigation__accordion">
                        {sections.map((section, index) => section.items.length === 1 ? renderItem(section.items[0]) : (
                            <div key={section.title} className="workspace-navigation__group">
                                <button type="button" className="workspace-navigation__group-toggle" aria-expanded={openSection === section.title}
                                    aria-controls={`workspace-section-${index}`} onClick={() => toggleSection(section.title)}>
                                    <span>{section.title}</span><ChevronDown size={16} className={openSection === section.title ? 'is-expanded' : ''} />
                                </button>
                                <div id={`workspace-section-${index}`} hidden={openSection !== section.title}>
                                    {section.items.map(renderItem)}
                                </div>
                            </div>
                        ))}
                    </div>}
                </nav>
                <div className="workspace-navigation__footer">
                    <NavLink to="/cabinet?tab=appearance" className="nav-link" onClick={onClose}><Settings size={18} /><span>Личные настройки</span></NavLink>
                </div>
            </aside>
        </>
    )
}
